import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { PropTypes } from 'prop-types';
import treeChanges from 'tree-changes';

import { AuditOutlined, EditOutlined, LaptopOutlined, ReloadOutlined } from '@ant-design/icons';

import { Row, Col, Table, Button, Badge } from 'antd';

import AntDesignTable from '../../Components/AntDesignTable';
import ItemList02Actions from '../../Stores/ItemList02/Actions';

class ItemList02Table extends React.PureComponent {
  constructor() {
    super();

    this.useOnTableChange = this.useOnTableChange.bind(this);
    this.useShowWorkspace = this.useShowWorkspace.bind(this);

    this.handleSearch = this.handleSearch.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.getDocumentColumns = this.getDocumentColumns.bind(this);
  }

  componentDidMount() {
    const { curSiteFlowId, setWorkspaceVisible, resetTimestamp } = this.props;

    if (curSiteFlowId > 0) {
      setWorkspaceVisible(false);
      resetTimestamp();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      curSiteFlowId,
      resetTimestamp,
      fetchItemList02,
      currentPage,
      sorts,
      filters,
      pageSize
    } = this.props;

    const { changed } = treeChanges(prevProps, this.props);

    if (changed('curSiteFlowId')) {
      resetTimestamp();
    }

    if (changed('timestamp')) {
      if (curSiteFlowId > 0) {
        fetchItemList02(currentPage, sorts, filters, pageSize);
      }
    }
  }

  componentWillUnmount() {}

  handleSearch(selectedKeys, confirm) {
    // eslint-disable-next-line no-unused-vars
    const { intl } = this.props;

    confirm();
  }

  handleReset(clearFilters) {
    // eslint-disable-next-line no-unused-vars
    const { intl } = this.props;

    clearFilters();
  }

  getDocumentColumns() {
    const { intl, sorts, filters, goToResource, goToAudit } = this.props;

    return [
      {
        width: 100,
        align: 'left',
        title: intl.formatMessage({ id: 'code' }),
        // sort field
        dataIndex: 'code',
        // filter field
        key: 'code',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'code' }),
          'code',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => <>{record.code}</>
      },
      {
        width: 150,
        align: 'left',
        title: intl.formatMessage({ id: 'description' }),
        // sort field
        dataIndex: 'desc_01',
        ...AntDesignTable.getColumnSortProps(sorts, 'desc_01'),
        // filter field
        key: 'desc_01',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'description' }),
          'desc_01',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => <>{record.desc_01}</>
      },
      {
        width: 100,
        align: 'left',
        title: intl.formatMessage({ id: 'brand' }),
        // sort field
        dataIndex: 'item_group01_code',
        ...AntDesignTable.getColumnSortProps(sorts, 'item_group01_code'),
        // filter field
        key: 'item_group01_code_in',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'brand' }),
          'item_group01_code_in',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => <>{record.item_group_01_code}</>
      },
      {
        width: 100,
        align: 'left',
        title: intl.formatMessage({ id: 'category' }),
        // sort field
        dataIndex: 'item_group02_code',
        ...AntDesignTable.getColumnSortProps(sorts, 'item_group02_code'),
        // filter field
        key: 'item_group02_code_in',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'category' }),
          'item_group02_code_in',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => <>{record.item_group_02_code}</>
      },
      {
        width: 100,
        align: 'left',
        title: intl.formatMessage({ id: 'manufacturer' }),
        // sort field
        dataIndex: 'item_group03_code',
        ...AntDesignTable.getColumnSortProps(sorts, 'item_group03_code'),
        // filter field
        key: 'item_group03_code_in',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'manufacturer' }),
          'item_group03_code_in',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => <>{record.item_group_03_code}</>
      },
      {
        width: 100,
        align: 'left',
        title: intl.formatMessage({ id: 'status' }),
        // sort field
        dataIndex: 'status',
        ...AntDesignTable.getColumnSortProps(sorts, 'status'),
        // filter field
        key: 'status',
        filters: [
          {
            text: 'DELETED',
            value: 1
          },
          {
            text: 'INACTIVE',
            value: 2
          },
          {
            text: 'BLOCKED',
            value: 3
          },
          {
            text: 'CONFLICT',
            value: 4
          },
          {
            text: 'NEW',
            value: 5
          },
          {
            text: 'WIP',
            value: 50
          },
          {
            text: 'ACTIVE',
            value: 100
          }
        ],
        render: (text, record) => <>{record.str_status}</>
      },
      {
        width: 100,
        align: 'right',
        title: intl.formatMessage({ id: 'case_uom_rate' }),
        // sort field
        dataIndex: 'case_uom_rate',
        ...AntDesignTable.getColumnSortProps(sorts, 'case_uom_rate'),
        // filter field
        key: 'case_uom_rate',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'case_uom_rate' }),
          'case_uom_rate',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => (
          <>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
              maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
            }).format(record.case_uom_rate)}
          </>
        )
      },
      {
        width: 110,
        align: 'right',
        title: intl.formatMessage({ id: 'case_ext_length' }),
        // sort field
        dataIndex: 'case_ext_length',
        ...AntDesignTable.getColumnSortProps(sorts, 'case_ext_length'),
        // filter field
        key: 'case_ext_length',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'case_ext_length' }),
          'case_ext_length',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => (
          <>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
              maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
            }).format(record.case_ext_length)}
          </>
        )
      },
      {
        width: 110,
        align: 'right',
        title: intl.formatMessage({ id: 'case_ext_width' }),
        // sort field
        dataIndex: 'case_ext_width',
        ...AntDesignTable.getColumnSortProps(sorts, 'case_ext_width'),
        // filter field
        key: 'case_ext_width',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'case_ext_width' }),
          'case_ext_width',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => (
          <>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
              maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
            }).format(record.case_ext_width)}
          </>
        )
      },
      {
        width: 110,
        align: 'right',
        title: intl.formatMessage({ id: 'case_ext_height' }),
        // sort field
        dataIndex: 'case_ext_height',
        ...AntDesignTable.getColumnSortProps(sorts, 'case_ext_height'),
        // filter field
        key: 'case_ext_height',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'case_ext_height' }),
          'case_ext_height',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => (
          <>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
              maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
            }).format(record.case_ext_height)}
          </>
        )
      },
      {
        width: 110,
        align: 'right',
        title: intl.formatMessage({ id: 'case_gross_weight' }),
        // sort field
        dataIndex: 'case_gross_weight',
        ...AntDesignTable.getColumnSortProps(sorts, 'case_gross_weight'),
        // filter field
        key: 'case_gross_weight',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'case_gross_weight' }),
          'case_gross_weight',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => (
          <>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
              maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
            }).format(record.case_gross_weight)}
          </>
        )
      },
      {
        width: 110,
        align: 'right',
        title: intl.formatMessage({ id: 'cases_per_layer' }),
        // sort field
        dataIndex: 'cases_per_pallet_length',
        ...AntDesignTable.getColumnSortProps(sorts, 'cases_per_pallet_length'),
        // filter field
        key: 'cases_per_pallet_length',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'cases_per_layer' }),
          'cases_per_pallet_length',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => (
          <>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
              maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
            }).format(record.cases_per_pallet_length)}
          </>
        )
      },
      {
        width: 110,
        align: 'right',
        title: intl.formatMessage({ id: 'no_of_layers' }),
        // sort field
        dataIndex: 'no_of_layers',
        ...AntDesignTable.getColumnSortProps(sorts, 'no_of_layers'),
        // filter field
        key: 'no_of_layers',
        ...AntDesignTable.getColumnSearchInputProps(
          filters,
          intl.formatMessage({ id: 'no_of_layers' }),
          'no_of_layers',
          this.handleSearch,
          this.handleReset
        ),
        render: (text, record) => (
          <>
            {new Intl.NumberFormat([], {
              style: 'decimal',
              minimumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE,
              maximumFractionDigits: process.env.REACT_APP_DECIMAL_SCALE
            }).format(record.no_of_layers)}
          </>
        )
      },
      {
        width: 50,
        fixed: 'right',
        title: '',
        key: 'action',
        render: (text, record) => (
          <>
            <Button type="dashed" icon={<EditOutlined />} onClick={() => goToResource(record.id)} />
            <Button type="dashed" icon={<AuditOutlined />} onClick={() => goToAudit(record.id)} />
          </>
        )
      }
    ];
  }

  useOnTableChange(pagination, filters, sorter) {
    const { resetTimestamp } = this.props;

    const processedFilters = AntDesignTable.processFilters(filters);
    const processedSorts = AntDesignTable.processSorts(sorter);

    resetTimestamp(pagination.current, processedSorts, processedFilters, pagination.pageSize);
  }

  useShowWorkspace() {
    const { setWorkspaceVisible } = this.props;

    setWorkspaceVisible(true);
  }

  render() {
    const {
      intl,
      curSiteFlowId,
      resetTimestamp,
      newResource,
      currentPage,
      pageSize,
      total,
      documents,
      fetchIsLoading,
      selectedDocuments,
      addSelectedDocuments,
      removeSelectedDocuments
    } = this.props;

    return (
      <>
        <Table
          size="small"
          // rowSelection={rowSelection}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: parseInt(pageSize, 10),
            total,
            showTotal: () => `${total} items`
          }}
          columns={this.getDocumentColumns()}
          dataSource={documents}
          loading={fetchIsLoading}
          bordered
          rowSelection={{
            selectedRowKeys: selectedDocuments.map(value => value.id),
            onChange: () => {
              // console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
            },
            onSelect: (record, selected) => {
              // console.log(`onSelect record: `, record, 'selected: ', selected, 'selectedRows: ', selectedRows);
              if (selected === true) {
                addSelectedDocuments([record]);
              } else {
                removeSelectedDocuments([record]);
              }
            },
            onSelectAll: (selected, selectedRows, changeRows) => {
              // console.log('onSelectAll selected: ', selected, 'selectedRows: ', selectedRows, 'changeRows: ', changeRows);
              if (selected === true) {
                addSelectedDocuments(changeRows);
              } else {
                removeSelectedDocuments(changeRows);
              }
            },
            onSelectInvert: selectedRows => {
              // console.log('onSelectInvert selectedRows: ', selectedRows);
              removeSelectedDocuments(selectedRows);
            },
            getCheckboxProps: () => ({
              // Column configuration not to be checked
              disabled: true
              // name: record.name,
            })
          }}
          onChange={this.useOnTableChange}
          title={() => (
            <>
              <Row type="flex" justify="start" gutter={[0, 16]}>
                <Col span={1}>
                  <Button
                    type="primary"
                    // disabled={!isValid}
                    loading={fetchIsLoading}
                    onClick={() => resetTimestamp()}
                    icon={<ReloadOutlined />}
                  />
                </Col>
                <Col span={5}>
                  <Button
                    type="primary"
                    disabled={curSiteFlowId === 0}
                    loading={fetchIsLoading}
                    onClick={() => newResource()}
                    // icon={<PlusOutlined />}
                  >
                    {intl.formatMessage({ id: 'new' })}
                  </Button>
                </Col>
              </Row>
            </>
          )}
          scroll={{ x: 950 }}
        />

        <div
          style={{
            position: 'fixed',
            bottom: '50px',
            right: '50px',
            zIndex: 9999
          }}
        >
          <Badge count={selectedDocuments.length}>
            <Button
              size="large"
              type="primary"
              shape="circle"
              icon={<LaptopOutlined />}
              onClick={this.useShowWorkspace}
              loading={fetchIsLoading}
            />
          </Badge>
        </div>
      </>
    );
  }
}

ItemList02Table.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  intl: PropTypes.object,
  resetTimestamp: PropTypes.func,
  goToResource: PropTypes.func,
  newResource: PropTypes.func,
  goToAudit: PropTypes.func,

  setWorkspaceVisible: PropTypes.func,

  addSelectedDocuments: PropTypes.func,
  removeSelectedDocuments: PropTypes.func,
  documents: PropTypes.arrayOf(PropTypes.object),
  selectedDocuments: PropTypes.arrayOf(PropTypes.object),

  sorts: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  filters: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),

  currentPage: PropTypes.number,
  pageSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  total: PropTypes.number,
  fetchIsLoading: PropTypes.bool,

  curSiteFlowId: PropTypes.number,
  fetchItemList02: PropTypes.func
};

ItemList02Table.defaultProps = {
  intl: {},
  resetTimestamp() {},
  goToResource() {},
  newResource() {},
  goToAudit() {},

  setWorkspaceVisible() {},

  addSelectedDocuments() {},
  removeSelectedDocuments() {},
  documents: [],
  selectedDocuments: [],

  sorts: {},
  filters: {},

  currentPage: 1,
  pageSize: '20',
  total: 0,
  fetchIsLoading: false,

  curSiteFlowId: 0,
  fetchItemList02() {}
};

const mapStateToProps = state => ({
  timestamp: state.itemList02.timestamp,
  workspaceIsVisible: state.itemList02.workspaceIsVisible,

  documents: state.itemList02.documents,
  selectedDocuments: state.itemList02.selectedDocuments,

  sorts: state.itemList02.sorts,
  filters: state.itemList02.filters,

  currentPage: state.itemList02.currentPage,
  pageSize: state.itemList02.pageSize,
  total: state.itemList02.total,
  fetchIsLoading: state.itemList02.fetchIsLoading,

  curSiteFlowId: state.app.curSiteFlowId
});

const mapDispatchToProps = dispatch => ({
  resetTimestamp: (currentPage, sorts, filters, pageSize) =>
    dispatch(ItemList02Actions.itemList02ResetTimestamp(currentPage, sorts, filters, pageSize)),
  goToResource: resId => dispatch(ItemList02Actions.itemList02GoToResource(resId)),
  newResource: () => dispatch(ItemList02Actions.itemList02NewResource()),
  goToAudit: hdrId => dispatch(ItemList02Actions.itemList02GoToAudit(hdrId)),

  setWorkspaceVisible: boolean =>
    dispatch(ItemList02Actions.itemList02SetWorkspaceVisible(boolean)),

  addSelectedDocuments: selectedDocuments =>
    dispatch(ItemList02Actions.itemList02AddSelectedDocuments(selectedDocuments)),
  removeSelectedDocuments: selectedDocuments =>
    dispatch(ItemList02Actions.itemList02RemoveSelectedDocuments(selectedDocuments)),

  fetchItemList02: (currentPage, sorts, filters, pageSize) =>
    dispatch(ItemList02Actions.itemList02FetchItemList02(currentPage, sorts, filters, pageSize))
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(ItemList02Table));
